import React, { useState, useMemo, useEffect } from 'react'

import {
  Container,
  Row,
  Col,
  RadioButton,
  LoadingSpinner,
} from '@paypalcorp/pp-react'
import _ from 'lodash'

import { search } from '../../components/video-lib/utils/search.server'
import { getTitleFromFilters } from '../../components/video-lib/utils/collectionFilters'
import { navigate } from 'gatsby'

import { PreviousPageLink } from '../../components/video-lib/components/PreviousPageLink'
import { VideoStrip } from '../../components/video-lib/sections/VideoStrip'
import VideoLayout from '../../components/video-lib/components/VideoLayout'
import { Video } from '../../components/video-lib/utils/common'
import videos from '../../components/video-lib/data/videos.json'
import useParams from '../../hooks/useParams'

const jsVideoIds = ['llGKcQPeaxg', 'tq9MfIG-VXw', '3_73S7kOW8U']

const getLabel = (key) => {
  let label
  switch (key) {
    case 'woocommerce':
      label = 'WooCommerce'
      break
    case 'bigcommerce':
      label = 'BigCommerce'
      break
    case 'boldcommerce':
      label = 'Bold Commerce'
      break
    case 'api':
      label = 'API'
      break
    default:
      label = key.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }
  label = label.replace(/Paypal/g, 'PayPal')
  return label
}

function Collection() {
  const { q: searchText, filter } = useParams()
  const expandVideoProductIds = (item) => {
    const ids = Array.isArray(item['video-Product-id'])
      ? item['video-Product-id']
      : [item['video-Product-id'] || 'others']
    return ids.map((id) => ({
      ...item,
      'video-Product-id': id.toLowerCase().replace(/\s+/g, ''),
    }))
  }
  const expandedVideos = videos.flatMap(expandVideoProductIds)
  const groupedByProductId = useMemo(
    () => _.groupBy(expandedVideos, 'video-Product-id'),
    [expandedVideos],
  )
  const productFilters = useMemo(() => {
    const filterKeys = Object.keys(groupedByProductId).filter(
      (k) => k !== 'others',
    )
    const filters = filterKeys.map((key, i) => ({
      label: getLabel(key),
      value: key,
      key: i,
    }))
    return [{ label: 'All Videos', value: 'all' }, ...filters]
  }, [groupedByProductId])

  const getDefaultFilter = (filter: string) => {
    const element = _.find(productFilters, { value: filter })
    return element ? element.value : 'all'
  }

  const [actionData, setActionData] = useState<Record<string, any> | null>(null)
  const [searchQuery, setSearchQuery] = useState(searchText || '')
  const [selectedProduct, setSelectedProduct] = useState(
    getDefaultFilter(filter),
  )
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    setIsLoading(true)
    const tempVideos = search({
      searchQuery,
      videos:
        selectedProduct && groupedByProductId[selectedProduct]
          ? groupedByProductId[selectedProduct]
          : videos,
    })
    setActionData({ videos: tempVideos })
    setIsLoading(false)
  }, [searchQuery, selectedProduct, searchText, videos])

  const filtertedVideos: Video[] = actionData ? actionData.videos : videos

  return (
    <>
      <div style={{ background: '#FAF8F5', padding: '2rem' }}>
        <Container>
          <Row>
            <Col md={6} sm={12}>
              <PreviousPageLink url="/video/home" label="Back to Home" />
              <h1 className="homepageTitle" data-testid="header-title">
                {getTitleFromFilters({
                  selectedProduct,
                  filters: productFilters,
                })}
              </h1>
            </Col>
            <Col md={6} sm={12} className="search-bar">
              <input
                data-testid="search-input"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                }}
                type="text"
                className="search"
                placeholder="Search by keyword"
                name="searchQuery"
                defaultValue={searchQuery}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <Container>
        <Row style={{ padding: '2rem 0' }}>
          <Col md={3} data-testid="filter-options">
            <div style={{ padding: '1rem 0' }} />

            <RadioButton
              name="product"
              label="Filter by Product"
              value={selectedProduct}
              options={productFilters}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const newSelectedProduct = e.target.value
                setSelectedProduct(newSelectedProduct)
                navigate(`/video/collection?filter=${newSelectedProduct}`)
              }}
            />
          </Col>
          <Col md={8} data-testid="video-strip-wrapper">
            {isLoading && (
              <div className="spinnerWrapper">
                <LoadingSpinner size="xl" />
              </div>
            )}
            {!isLoading &&
              filtertedVideos
                .filter((video) => !jsVideoIds.includes(video.id))
                .map((video, i) => (
                  <VideoStrip
                    key={i}
                    title={video.title}
                    duration={video.duration}
                    url={`/video/watch?videoId=${video?.id}`}
                    thumbnail={video?.thumbnails?.high?.url}
                  />
                ))}
            {!isLoading && filtertedVideos?.length === 0 && (
              <h2 style={{ display: 'flex' }}>No Results found</h2>
            )}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default function VideoWrapper() {
  return (
    <VideoLayout>
      <Collection />
    </VideoLayout>
  )
}
